import { cva } from "class-variance-authority";

// AIDEV-NOTE: Button variants using new brand color scheme - forest green primary, terracotta secondary
export const buttonVariants = cva(
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    {
        variants: {
            variant: {
                default:
                    "border-brand-terra-200 border text-brand-terra-600 bg-brand-terra-100/50 hover:bg-brand-terra-100/90 rounded-[20px]",
                forest: "border-brand-forest-200 border text-brand-forest-600 bg-brand-forest-100/50 hover:bg-brand-forest-100/90 rounded-[20px]",
                // AIDEV-NOTE: solidForest = primary CTA in new design system showcase.
                // Solid forest-800 fill with white text — used for "Sign in" and primary action pills.
                solidForest:
                    "bg-brand-forest-800 text-white hover:bg-brand-forest-700 rounded-[20px]",
                // AIDEV-NOTE: neutral = secondary CTA in new design system showcase.
                // White surface with forest-200 border + forest-800 text — used for "Cancel" and secondary action pills.
                neutral:
                    "bg-white border border-brand-forest-200 text-brand-forest-800 hover:bg-brand-forest-50 rounded-[20px]",
                forestOutline:
                    "border border-brand-forest-200 bg-white text-brand-forest-800 hover:bg-brand-forest-50 hover:text-brand-forest-800 rounded-[20px]",
                internal:
                    "bg-blue-300 text-blue-900 hover:bg-blue-400 border-blue-900 border rounded-[20px]",
                circle: "border-brand-terra-200 border text-brand-terra-600 bg-white hover:bg-brand-terra-100/90 border-brand-terra-100 rounded-[20px]",
                subtle: "border-brand-terra-200 border text-brand-terra-600 bg-brand-terra-50/30 hover:bg-brand-terra-100/60 rounded-[20px] transition-colors",
                destructive:
                    "border border-destructive bg-background text-destructive hover:bg-accent hover:text-accent-foreground rounded-[20px]",
                outline:
                    "border border-input bg-background border-brand-terra-100 text-brand-terra-600 hover:bg-brand-terra-100/90 hover:text-brand-terra-600 rounded-[20px]",
                secondary:
                    "bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-[20px]",
                ghost: "hover:bg-brand-terra-100/90 hover:text-brand-terra-600 text-brand-terra-600 rounded-[20px]",
                link: "text-brand-forest-800 underline underline-offset-4 hover:text-brand-forest-700",
                ghostIcon:
                    "hover:bg-brand-terra-100/90 hover:text-brand-terra-600 text-brand-terra-600 rounded-[20px]",
                actionIcon:
                    "bg-brand-terra-100/50 text-brand-terra-600 border border-brand-terra-100 hover:bg-brand-terra-100 rounded-[20px]",
                onDark: "bg-brand-terra-600 text-white hover:bg-brand-terra-700 rounded-[20px]",
                // AIDEV-NOTE: magic = gradient text only, magic-gradient = gradient background + gradient text
                // Both variants get rainbow text via bg-clip-text span in button.tsx
                magic: "hover:bg-purple-100 [&_svg]:text-purple-600 hover:[&_svg]:text-purple-500 rounded-[20px] disabled:opacity-40",
                "magic-gradient":
                    "border border-purple-200/50 bg-gradient-to-r from-purple-100/50 via-pink-100/50 to-emerald-100/50 font-medium hover:from-purple-100 hover:via-pink-100 hover:to-emerald-100 transition-all rounded-[20px] disabled:opacity-40",
                // AIDEV-NOTE: design-system-v2 "chrome" button family — dense app-chrome buttons
                // used inside product UI (toolbars, table rows, panel headers). Distinct from the
                // 20px capsule pills used in marketing / onboarding. Use with size="chrome" so the
                // 10px radius (rounded-button) and 13px label stick. See design-system-v2.jsx DSButton.
                chromePrimary:
                    "bg-brand-forest-800 text-white hover:bg-brand-forest-700 rounded-button shadow-cta",
                chromeSecondary:
                    "bg-white text-brand-forest-800 border border-brand-forest-200 hover:bg-brand-forest-50 rounded-button",
                chromeGhost:
                    "bg-transparent text-brand-forest-700 hover:bg-brand-forest-50 rounded-button",
                // AIDEV-NOTE: sidebarPrimary = full-width primary CTA inside a
                // secondary sidebar nav rail. Mirrors Clients Admin handoff:
                // centered label, forest-800 fill, 10px radius, forest CTA shadow.
                // Pair with `size="sidebar"`; Sidebar.NavItem layers active ring.
                sidebarPrimary:
                    "w-full justify-center border border-transparent bg-brand-forest-800 text-white shadow-cta hover:bg-brand-forest-700 rounded-button",
            },
            size: {
                default: "h-10 px-4 py-2 rounded-[20px]",
                sm: "h-9 px-3 rounded-[18px]",
                lg: "h-11 px-8 rounded-[22px]",
                icon: "h-10 w-10 px-4 rounded-[18px]",
                // AIDEV-NOTE: Action pills used inside StatusCard, StandardCard, and detail rows.
                // 28px height, 12px label, 13px icon — matches the StatusCard action footprint.
                pill: "h-7 px-3 text-xs gap-1 rounded-[20px] [&_svg]:size-3.5",
                // AIDEV-NOTE: design-system-v2 dense app-chrome size — 36px height, 13px label,
                // 14px icon, 10px radius. Pairs with chromePrimary / chromeSecondary / chromeGhost.
                chrome: "h-9 px-4 py-[9px] text-[13px] rounded-button [&_svg]:size-3.5",
                // AIDEV-NOTE: sidebar = Clients Admin handoff size for sidebar
                // primary CTAs: 13px/600 text, 10px y + 12px x padding, 15px icon.
                sidebar:
                    "px-3 py-2.5 text-[13px] font-semibold leading-5 tracking-[-0.05px] gap-2 rounded-button [&_svg]:size-[15px]",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    },
);
